'use client';

import type { InputProps } from '../input';

import { forwardRef, useEffect, useRef, useState } from 'react';
import Image from 'next/image';

import { useCursor } from '@paid-ui/hooks/use-cursor';
import { useIsomorphicEffect } from '@paid-ui/hooks/use-isomorphic-effect';
import { useMergedRef } from '@paid-ui/hooks/use-merged-ref';
import { formatMobileNumber, getCountryFlagUrl } from '@paid-ui/utils/mobile-number';

import { Input } from '../input';

const MAX_LENGTH = 15;
const INPUT_REGEXP = /^[\d\s+]{0,15}$/;
const REPLACE_REGEXP = /[^\d+]+/g;
const cleanValue = (value: string) => value.replaceAll(REPLACE_REGEXP, '').slice(0, 12);

export interface MobileInputProps
  extends Omit<InputProps, 'type' | 'inputMode' | 'maxLength' | 'autoCapitalize' | 'autoCorrect'> {
  value?: string;
  defaultValue?: string;
  hideFlag?: boolean;
}

export const MobileInput = forwardRef<HTMLInputElement, MobileInputProps>(
  (
    {
      value,
      defaultValue,
      hideFlag = true,
      autoComplete = 'off',
      onChange,
      onFocus,
      onBlur,
      onPaste,
      className,
      ...restProps
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [focus, setFocus] = useState(false);
    const [recordCursor, restoreCursor] = useCursor(inputRef.current, focus);
    const [displayValue, setDisplayValue] = useState(
      () => formatMobileNumber(value ?? defaultValue) ?? '',
    );

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
      setFocus(true);
      const targetValue = cleanValue(event.currentTarget.value);
      event.currentTarget.value = targetValue;
      onFocus?.(event);
      setDisplayValue(formatMobileNumber(targetValue) ?? '');
    };

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (event) => {
      setFocus(false);
      const targetValue = cleanValue(event.currentTarget.value);
      event.currentTarget.value = targetValue;
      onBlur?.(event);
      setDisplayValue(formatMobileNumber(targetValue) ?? '');
    };

    const handlePaste: React.ClipboardEventHandler<HTMLInputElement> = (event) => {
      const pastedValue = event.clipboardData.getData('text/plain');
      const targetValue = cleanValue(pastedValue);
      event.currentTarget.value = targetValue;
      onPaste?.(event);
      setDisplayValue(formatMobileNumber(targetValue) ?? '');
      // @ts-expect-error - Event type is not compatible.
      onChange?.(event);
      event.preventDefault();
    };

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      // 1. Record the cursor position and related values.
      recordCursor();

      // 2. Get cleaned input value and formatted display value.
      const targetValue = cleanValue(event.currentTarget.value);
      const newDisplayValue = formatMobileNumber(targetValue) ?? '';

      // 3. If the input is empty, set the display value to empty and call onChange.
      if (!targetValue) {
        setDisplayValue('');
        onChange?.(event);
        return;
      }

      // 4. If the input is invalid, do nothing.
      // 5. If th new display value is the same as the old one, do nothing.
      if (!INPUT_REGEXP.test(targetValue) || newDisplayValue === displayValue) {
        return;
      }

      // 6. Set the display value to the new one and call onChange.
      setDisplayValue(newDisplayValue);
      event.currentTarget.value = targetValue;
      onChange?.(event);
    };

    useIsomorphicEffect(() => {
      // 7. Restore the cursor position if the display value has changed.
      restoreCursor();
    }, [displayValue]);

    useEffect(() => {
      const displayValue = formatMobileNumber(value) ?? '';
      setDisplayValue(displayValue);
    }, [value]);

    return (
      <Input
        ref={useMergedRef(inputRef, ref)}
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onPaste={handlePaste}
        autoComplete={autoComplete}
        className={className}
        {...restProps}
        type="tel"
        inputMode="numeric"
        maxLength={MAX_LENGTH}
        autoCapitalize="none"
        autoCorrect="off"
        prefix={
          hideFlag ? null : (
            <Image
              width={24}
              height={24}
              alt="Country flag"
              src={getCountryFlagUrl(displayValue)}
            />
          )
        }
      />
    );
  },
);

MobileInput.displayName = 'MobileInput';
